/**
 * DI-3-S61 §2.2 — Settings → History: the words of the modal, pure. Fixed
 * `en` words, no `toLocale*`, no `Intl` (the intake surfaces' rule).
 *
 * The wire is intake/v1 1.44.0 `getIntakeHistory` (`IntakeHistory`): the
 * events newest LAST, and the CURRENT coverage per blade side with each gap's
 * acknowledgement. The FE renders what is sent: an actor's `name` is the
 * server's outcome of the `show_user_names` rule (a foreign actor arrives as
 * "<Tenant> user") — there is no name logic here.
 *
 * COPIED RECIPES (`apps/lyse` imports nothing from `apps/web` — no lyse file
 * does — so the recipe is copied and named):
 *   - `instantLabel` / `dayLabel`  ← `apps/web/src/features/intake/board/openSets.ts`
 *   - `cameraTime` + `inspectedOn` ← `apps/web/src/features/intake/decide/images/cameraTime.ts`
 *     and `…/verify/details.ts:inspectedOn` (DI-3-S53b round 15: the span of
 *     the captures on the camera clock as written, else the earliest instant's day)
 *   - `sideChip` ← the DI-3-S25 chip (`…/verify/coverage.ts:coverageChip`) in
 *     its no-limits branch: the history wire carries no thresholds, so a side
 *     with an open blind spot reads "attention" (never "critical") — see the
 *     story's FE hand-back.
 *   - `bladeUnitLabels` ← DI-3-S76 `…/verify/coverage.ts:coverageUnits` /
 *     `bladeGroupLabel` (a real blade group — several blades at one position
 *     or one blade on several — named "Blade A · top" / "Blade 2 · top"; the
 *     history wire carries no points, so a blade without a serial is named by
 *     its number only).
 */
import type { IntakeComponents } from "@aeriosiq/types";
import type { StatusSlot } from "@aeriosiq/ui";

export type IntakeHistory = IntakeComponents["schemas"]["IntakeHistory"];
export type IntakeHistoryEvent =
  IntakeComponents["schemas"]["IntakeHistoryEvent"];
export type IntakeHistoryCoverage =
  IntakeComponents["schemas"]["IntakeHistoryCoverage"];
export type IntakeHistoryCoverageSide =
  IntakeComponents["schemas"]["IntakeHistoryCoverageSide"];
export type IntakeHistoryCoverageGap =
  IntakeComponents["schemas"]["IntakeHistoryCoverageGap"];
export type CoverageApproval = IntakeComponents["schemas"]["CoverageApproval"];

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "June",
  "July",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
] as const;

const pad = (n: number, w = 2): string => String(n).padStart(w, "0");

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** "Sat 24 Oct" (the year added when it is not this year's) — the viewer's zone. */
export function dayLabel(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const at = parse(iso);
  if (!at) return null;
  const base = `${WEEKDAY[at.getDay()] ?? ""} ${String(at.getDate())} ${MONTH[at.getMonth()] ?? ""}`;
  return at.getFullYear() === now.getFullYear()
    ? base
    : `${base} ${String(at.getFullYear())}`;
}

/** "Sat 24 Oct 09:31" — `instantLabel` of the intake board, the viewer's zone. */
export function instantLabel(
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const at = parse(iso);
  if (!at) return null;
  return `${dayLabel(iso, now) ?? ""} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** The camera clock as written ("14:45:46.120") — never converted to a zone (DI-D18 c1). */
export function cameraTime(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${pad(d.getUTCMilliseconds(), 3)}`;
}

/**
 * INSPECTED ON (DI-3-S53b round 15) — with capture instants, their span on
 * the camera clock: "Tue 20 Oct · 14:45–15:04" (one time within a minute;
 * "Tue 20 Oct 14:45 – Wed 21 Oct 09:12" across dates). Without them, the
 * earliest of `instants` as a day ("Sun 19 Oct"). null → no item.
 */
export function inspectedOn(
  instants: readonly (string | null | undefined)[],
  now: Date,
  captures?: readonly (string | null | undefined)[] | null,
): string | null {
  let lo: number | null = null;
  let hi: number | null = null;
  for (const c of captures ?? []) {
    const t = c ? Date.parse(c) : Number.NaN;
    if (!Number.isFinite(t)) continue;
    if (lo === null || t < lo) lo = t;
    if (hi === null || t > hi) hi = t;
  }
  if (lo !== null && hi !== null) {
    const day = (ms: number): string => {
      const d = new Date(ms);
      const base = `${WEEKDAY[d.getUTCDay()] ?? ""} ${String(d.getUTCDate())} ${MONTH[d.getUTCMonth()] ?? ""}`;
      return d.getUTCFullYear() === now.getFullYear()
        ? base
        : `${base} ${String(d.getUTCFullYear())}`;
    };
    const hm = (ms: number): string => cameraTime(ms).slice(0, 5);
    const dayKey = (ms: number): string =>
      new Date(ms).toISOString().slice(0, 10);
    if (dayKey(lo) !== dayKey(hi))
      return `${day(lo)} ${hm(lo)} – ${day(hi)} ${hm(hi)}`;
    return hm(lo) === hm(hi)
      ? `${day(lo)} · ${hm(lo)}`
      : `${day(lo)} · ${hm(lo)}–${hm(hi)}`;
  }
  let best: number | null = null;
  for (const i of instants) {
    if (!i) continue;
    const t = Date.parse(i);
    if (Number.isFinite(t) && (best === null || t < best)) best = t;
  }
  if (best === null) return null;
  return dayLabel(new Date(best).toISOString(), now);
}

/** The actor as the wire names it; null for the pipeline / a row with no one to name. */
export function actorName(e: Pick<IntakeHistoryEvent, "actor">): string | null {
  return e.actor?.name ?? null;
}

/** "Ada Pilot" — the `created` event's actor; null without one (or no name). */
export function createdBy(
  events: readonly IntakeHistoryEvent[],
): string | null {
  const c = events.find((e) => e.kind === "created");
  return c ? actorName(c) : null;
}

export type HeadStateKind = "approved" | "sent_back" | "withdrawn" | "open";

export interface HeadState {
  kind: HeadStateKind;
  text: string;
}

const STATE_KINDS: ReadonlySet<string> = new Set([
  "approved",
  "sent_back",
  "reopened",
  "withdrawn",
]);

/**
 * The head's state: the LAST of approved / sent back / reopened / withdrawn
 * decides — "approved by Ada Pilot on Sat 24 Oct", "sent back by Pia
 * Prescreener on Sun 25 Oct", "withdrawn on …"; a reopen (or none of them)
 * reads "open". Events are newest last (the wire's order).
 */
export function headState(
  events: readonly IntakeHistoryEvent[],
  now: Date = new Date(),
): HeadState {
  let last: IntakeHistoryEvent | null = null;
  for (const e of events) if (STATE_KINDS.has(e.kind)) last = e;
  if (!last || last.kind === "reopened") return { kind: "open", text: "open" };
  const who = actorName(last);
  const on = dayLabel(last.at, now);
  const tail = `${who ? ` by ${who}` : ""}${on ? ` on ${on}` : ""}`;
  if (last.kind === "approved")
    return { kind: "approved", text: `approved${tail}` };
  if (last.kind === "sent_back")
    return { kind: "sent_back", text: `sent back${tail}` };
  return { kind: "withdrawn", text: `withdrawn${on ? ` on ${on}` : ""}` };
}

type BladeOfSide = NonNullable<IntakeHistoryCoverageSide["blade"]>;
interface SideLike {
  bladePosition: string;
  blade?: Pick<BladeOfSide, "componentId" | "serial" | "positions"> | undefined;
}

const BOARD = ["top", "left", "right"];
const boardRank = (p: string): number => {
  const i = BOARD.indexOf(p);
  return i < 0 ? BOARD.length : i;
};

/**
 * DI-3-S76 C4 — the blade groups' words by component: a REAL blade group
 * only (another side at its position names another blade, or the blade is on
 * several positions) — "Blade A · top", "Blade B · top + left", a blade
 * without a serial "Blade 2 · top" (its place among the groups in the
 * server's order). Per-position sides get none.
 */
export function bladeUnitLabels(
  sides: readonly SideLike[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const s of sides) {
    const b = s.blade;
    if (!b) continue;
    const cid = b.componentId.toLowerCase();
    if (out.has(cid)) continue;
    const real =
      b.positions.length > 1 ||
      sides.some(
        (w) =>
          w.bladePosition === s.bladePosition &&
          (w.blade?.componentId.toLowerCase() ?? null) !== cid,
      );
    if (!real) continue;
    const where = [...b.positions]
      .sort((x, y) => boardRank(x) - boardRank(y))
      .join(" + ");
    const serial = b.serial?.trim() ?? "";
    out.set(
      cid,
      serial !== ""
        ? `Blade ${serial} · ${where}`
        : `Blade ${String(out.size + 1)} · ${where}`,
    );
  }
  return out;
}

/**
 * "TOP · SS" — the blade as the flight cards write it, the side as sent;
 * DI-3-S76 C4: a blade group's side "Blade A · top · SS" (`labels` from
 * `bladeUnitLabels` over the same verdict's sides).
 */
export function sideTitle(
  s: Pick<IntakeHistoryCoverageSide, "bladePosition" | "side"> & {
    blade?: Pick<BladeOfSide, "componentId"> | undefined;
  },
  labels: ReadonlyMap<string, string> = new Map(),
): string {
  const label = s.blade
    ? labels.get(s.blade.componentId.toLowerCase())
    : undefined;
  return `${label ?? s.bladePosition.toUpperCase()} · ${s.side}`;
}

/** "1.1–3.4 m" — metres from the rotor axis, to the decimetre. */
export function metreRange(g: { fromM: number; toM: number }): string {
  return `${g.fromM.toFixed(1)}–${g.toM.toFixed(1)} m`;
}

export function gapKindWord(kind: "blind" | "possible"): string {
  return kind === "blind" ? "blind spot" : "possible gap";
}

/** Blind spots first, then possible gaps; within a kind from the axis out. */
export function orderedGaps(
  gaps: readonly IntakeHistoryCoverageGap[],
): IntakeHistoryCoverageGap[] {
  return [...gaps].sort((a, b) =>
    a.kind === b.kind ? a.fromM - b.fromM : a.kind === "blind" ? -1 : 1,
  );
}

/**
 * One gap's line: "blind spot · 1.1–3.4 m — acknowledged by Ada Pilot on
 * Sat 24 Oct · root flange" or "possible gap · 52.0–53.1 m — not
 * acknowledged".
 */
export function gapLine(
  g: IntakeHistoryCoverageGap,
  now: Date = new Date(),
): string {
  const head = `${gapKindWord(g.kind)} · ${metreRange(g)}`;
  const a = g.acknowledgement;
  if (!a) return `${head} — not acknowledged`;
  const who = a.by.name ?? a.by.placeholder ?? "a pilot";
  const on = dayLabel(a.at, now);
  const note = a.note ? ` · ${a.note}` : "";
  return `${head} — acknowledged by ${who}${on ? ` on ${on}` : ""}${note}`;
}

/**
 * "still present after the re-run of Sat 24 Oct" — an acknowledged gap whose
 * verdict was recomputed since (`afterRerun`) and that is still in it
 * (`present`); the re-run's day is the current verdict's `checkedAt`. null
 * otherwise.
 */
export function stillPresentLine(
  g: IntakeHistoryCoverageGap,
  checkedAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const a = g.acknowledgement;
  if (!a?.present || !a.afterRerun) return null;
  const on = dayLabel(checkedAt, now);
  return `still present after the re-run${on ? ` of ${on}` : ""}`;
}

export interface SideChip {
  tone: StatusSlot;
  label: string;
}

const plural = (n: number, one: string, many: string): string =>
  `${String(n)} ${n === 1 ? one : many}`;

/**
 * The side's rating chip — the DI-3-S25 recipe over the side's OPEN gaps
 * (the acknowledged ones are named, never rated, DI-3-S55), in its
 * no-limits branch (the history wire carries no thresholds):
 *   open blind spot(s)  attention "2 blind spots"
 *   none open           success "no blind spot" / "no open blind spot"
 *   + " · n approved" when blind spots of the side are acknowledged
 *   no images, no gaps  neutral "no images" (`coveredShare` null)
 */
export function sideChip(s: IntakeHistoryCoverageSide): SideChip {
  const blind = s.gaps.filter((g) => g.kind === "blind");
  const open = blind.filter((g) => g.acknowledgement === null).length;
  const approved = blind.length - open;
  if (blind.length === 0 && s.coveredShare === null)
    return { tone: "neutral", label: "no images" };
  const base =
    open > 0
      ? plural(open, "blind spot", "blind spots")
      : approved > 0
        ? "no open blind spot"
        : "no blind spot";
  return {
    tone: open > 0 ? "attention" : "success",
    label: approved > 0 ? `${base} · ${String(approved)} approved` : base,
  };
}

/** "95.5 % covered"; null without a share. */
export function coveredLine(
  s: Pick<IntakeHistoryCoverageSide, "coveredShare">,
): string | null {
  if (s.coveredShare === null) return null;
  const pct = Math.round(s.coveredShare * 1000) / 10;
  return `${String(pct)} % covered`;
}

/** The effective approval rule (S55's `approvalRule`) in one quiet line. */
export function approvalRuleLine(a: CoverageApproval): string {
  switch (a.approvalRule) {
    case "blind_spots":
      return "Approval rule: every blind spot must be acknowledged before Approve";
    case "all_gaps":
      return "Approval rule: every blind spot and possible gap must be acknowledged before Approve";
    default:
      return "Approval rule: gaps do not hold Approve back";
  }
}

/** The kinds whose details expand (a verdict: the counts per side; a run: attempt, state, error). */
export function hasDetails(e: Pick<IntakeHistoryEvent, "kind">): boolean {
  return e.kind === "verdict" || e.kind === "match_run";
}

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const str = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;

/** A verdict event's `blade` (details are an open object — read defensively). */
function bladeOf(
  v: unknown,
): Pick<BladeOfSide, "componentId" | "serial" | "positions"> | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as Record<string, unknown>;
  const componentId = str(o.componentId);
  if (componentId === null) return undefined;
  const positions = Array.isArray(o.positions)
    ? o.positions.filter(
        (x): x is BladeOfSide["positions"][number] =>
          x === "top" || x === "left" || x === "right",
      )
    : [];
  return {
    componentId,
    serial: typeof o.serial === "string" ? o.serial : null,
    positions,
  };
}

/**
 * The expandable lines of a verdict ("TOP · SS — 2 blind spots, 1 possible
 * gap") or a run ("attempt 1", "state dead", "error extract timed out");
 * `details` is read defensively (the contract: "never parsed for a
 * decision" — these are words only).
 */
export function detailLines(e: IntakeHistoryEvent): string[] {
  const d = e.details;
  if (e.kind === "verdict") {
    const sides: unknown[] = Array.isArray(d.sides) ? d.sides : [];
    // DI-3-S76 C4 — a blade group's sides by its words
    const labels = bladeUnitLabels(
      sides.flatMap((raw) => {
        const s = (raw ?? {}) as Record<string, unknown>;
        const pos = str(s.bladePosition);
        return pos ? [{ bladePosition: pos, blade: bladeOf(s.blade) }] : [];
      }),
    );
    return sides.map((raw) => {
      const s = (raw ?? {}) as Record<string, unknown>;
      const b = Array.isArray(s.blindSpots) ? s.blindSpots.length : 0;
      const p = Array.isArray(s.possibleGaps) ? s.possibleGaps.length : 0;
      const blade = bladeOf(s.blade);
      const label = blade
        ? labels.get(blade.componentId.toLowerCase())
        : undefined;
      const title = `${label ?? (str(s.bladePosition) ?? "?").toUpperCase()} · ${str(s.side) ?? "?"}`;
      return `${title} — ${plural(b, "blind spot", "blind spots")}, ${plural(p, "possible gap", "possible gaps")}`;
    });
  }
  if (e.kind === "match_run") {
    const out: string[] = [];
    const attempt = num(d.attempt);
    if (attempt !== null) out.push(`attempt ${String(attempt)}`);
    const state = str(d.state);
    if (state) out.push(`state ${state}`);
    const error = str(d.error);
    if (error) out.push(`error ${error}`);
    return out;
  }
  return [];
}

/** A run that stopped (failed / dead / stalled). */
export function isStoppedRun(e: IntakeHistoryEvent): boolean {
  if (e.kind !== "match_run") return false;
  const state = e.details.state;
  return state === "failed" || state === "dead" || state === "stalled";
}
